import { useEffect } from 'react';

const SEOHead = ({ 
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  article = null
}) => {
  const siteUrl = 'https://venkatswaroop.in';
  const defaultImage = 'https://customer-assets.emergentagent.com/job_leader-showcase-2/artifacts/4mwtsfwz_IMG_20251217_170234.png';
  
  // Default values for homepage
  const defaultTitle = 'Venkat Swaroop | Global Talent Acquisition Leader | Top Recruiter & Mentor';
  const defaultDescription = 'Venkat Swaroop - Award-winning Global Talent Acquisition Leader with 15+ years experience. Served 100+ Fortune 500 clients, trained 1000+ recruiters across US, UK, EMEA, India, Australia & Canada. Top Staffing Leader 2024-2025. Expert in RPO, MSP, Executive Search & 360 Recruitment.';
  const defaultKeywords = 'Venkat Swaroop, Venkata Jyothi Swaroop Vemparala, Global Talent Acquisition Leader, Best Recruiters, Top Mentors, Innovative Leaders, Fortune 500 Recruitment, RPO Expert, Contingent Staffing, MSP Management, Executive Search, TekWissen, Recruiter IQ, Best Recruiters India, Top Talent Leaders, Recruitment Consultant, Staffing Solutions, HR Leadership, Talent Management, 360 Recruitment, Global Recruitment Expert, Award Winning Recruiter, Top Staffing Leaders 2025, Forbes HR Leader, Recruitment Mentor, Talent Acquisition Strategy, Workforce Solutions, Tech Recruitment, Healthcare Recruitment, Finance Recruitment, IT Staffing';

  const pageTitle = title || defaultTitle;
  const pageDescription = description || defaultDescription;
  const pageKeywords = keywords || defaultKeywords;
  const pageImage = image || defaultImage;
  const pageUrl = url || siteUrl;

  useEffect(() => {
    // Update document title
    document.title = pageTitle;

    // Helper to update or create meta tag
    const setMeta = (attr, attrValue, content) => {
      let element = document.querySelector(`meta[${attr}="${attrValue}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, attrValue);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Primary Meta Tags
    setMeta('name', 'title', pageTitle);
    setMeta('name', 'description', pageDescription);
    setMeta('name', 'keywords', pageKeywords);
    setMeta('name', 'author', 'Venkat Swaroop');
    setMeta('name', 'robots', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');

    // Open Graph / Facebook
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:url', pageUrl);
    setMeta('property', 'og:title', pageTitle);
    setMeta('property', 'og:description', pageDescription);
    setMeta('property', 'og:image', pageImage);
    setMeta('property', 'og:image:width', '1200');
    setMeta('property', 'og:image:height', '630');
    setMeta('property', 'og:site_name', 'Venkat Swaroop Portfolio');
    setMeta('property', 'og:locale', 'en_US');

    // Twitter
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:url', pageUrl);
    setMeta('name', 'twitter:title', pageTitle);
    setMeta('name', 'twitter:description', pageDescription);
    setMeta('name', 'twitter:image', pageImage);

    // Article specific tags
    if (article) {
      setMeta('property', 'article:author', article.author || 'Venkat Swaroop');
      if (article.publishedDate) {
        setMeta('property', 'article:published_time', article.publishedDate);
      }
      if (article.modifiedDate) {
        setMeta('property', 'article:modified_time', article.modifiedDate);
      }
      if (article.category) {
        setMeta('property', 'article:section', article.category);
      }
    }

    // Geographic Meta Tags
    setMeta('name', 'geo.region', 'IN');
    setMeta('name', 'geo.placename', 'India');

    // Additional Meta Tags
    setMeta('name', 'language', 'English');
    setMeta('name', 'revisit-after', '7 days');
    setMeta('name', 'distribution', 'global');
    setMeta('name', 'rating', 'general');

    // Update or create canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', pageUrl);

    // Per-page JSON-LD (Article) injected into a DEDICATED script element so it never
    // overwrites the canonical static "#ld-base" knowledge graph in index.html.
    const dynamicGraph = [];
    if (article) {
      dynamicGraph.push({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": article.title,
        "description": article.excerpt,
        "image": article.image || defaultImage,
        "author": {
          "@type": "Person",
          "@id": "https://venkatswaroop.in/#person",
          "name": article.author || "Venkat Swaroop"
        },
        "publisher": { "@id": "https://venkatswaroop.in/#person" },
        "datePublished": article.publishedDate,
        "dateModified": article.modifiedDate || article.publishedDate,
        "mainEntityOfPage": { "@type": "WebPage", "@id": pageUrl }
      });
    }

    let jsonLdScript = document.getElementById('ld-dynamic');
    if (dynamicGraph.length > 0) {
      if (!jsonLdScript) {
        jsonLdScript = document.createElement('script');
        jsonLdScript.type = 'application/ld+json';
        jsonLdScript.id = 'ld-dynamic';
        document.head.appendChild(jsonLdScript);
      }
      jsonLdScript.textContent = JSON.stringify(dynamicGraph.length === 1 ? dynamicGraph[0] : dynamicGraph);
    } else if (jsonLdScript) {
      // No article on this page — clear any stale dynamic schema from prior navigation
      jsonLdScript.textContent = '';
    }

    // Cleanup function - we don't remove tags as they should persist
  }, [pageTitle, pageDescription, pageKeywords, pageImage, pageUrl, type, article]);

  // This component doesn't render anything visible
  return null;
};

export default SEOHead;
